import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useLocalStorage from '../hooks/useLocalStorage';
import { Trash2, HelpCircle, Plus, Play, AlertTriangle, X, Download, Upload, Clock, Image as ImageIcon } from 'lucide-react';
import { reorderAnswers } from '../utils/reorderAnswers';

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="bg-red-100 p-3 rounded-full text-red-600">
                  <AlertTriangle size={24} />
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onConfirm();
                    onClose();
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 rounded-lg font-semibold text-white hover:bg-red-700 transition-colors"
                >
                  Reset All
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const LandingPage = () => {
  const [teams, setTeams] = useLocalStorage('feud-teams', [
    { name: 'Team 1', players: [], score: 0 },
    { name: 'Team 2', players: [], score: 0 }
  ]);
  const [questions, setQuestions] = useLocalStorage('feud-questions', []);
  const [timerDuration, setTimerDuration] = useLocalStorage('feud-timer-duration', 30);
  const [backgroundImageUrl, setBackgroundImageUrl] = useLocalStorage('feud-background-image-url', '');
  const [theme, setTheme] = useLocalStorage('feud-theme', 'default');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  React.useEffect(() => {
    document.title = "Popular Vote - Settings";
  }, []);

  const navigate = useNavigate();

  const handleReset = () => {
    setTeams([
      { name: 'Team 1', players: [], score: 0 },
      { name: 'Team 2', players: [], score: 0 }
    ]);
    setQuestions([]);
    setTimerDuration(30);
    setBackgroundImageUrl('');
    setTheme('default');
  };

  const exportGame = () => {
    const exportedTeams = teams.map(({ score, ...team }) => team);
    const data = { teams: exportedTeams, questions, timerDuration, backgroundImageUrl, theme };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `family-feud-game.feud.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const importGame = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target.result);
          if (data.teams && data.questions) {
            const importedTeams = data.teams.map(team => ({
              ...team,
              score: 0
            }));
            setTeams(importedTeams);
            setQuestions(data.questions);
            if (data.timerDuration !== undefined) {
              setTimerDuration(data.timerDuration);
            }
            if (data.backgroundImageUrl !== undefined) {
              setBackgroundImageUrl(data.backgroundImageUrl);
            }
            if (data.theme !== undefined) {
              setTheme(data.theme);
            }
          } else {
            alert('Invalid game file format.');
          }
        } catch (error) {
          alert('Error parsing the game file.');
        }
      };
      reader.readAsText(file);
    }
  };

  const addPlayer = (teamIndex) => {
    const newTeams = [...teams];
    newTeams[teamIndex].players.push({ name: '', image: null });
    setTeams(newTeams);
  };

  const updatePlayer = (teamIndex, playerIndex, field, value) => {
    const newTeams = [...teams];
    newTeams[teamIndex].players[playerIndex][field] = value;
    setTeams(newTeams);
  };

  const removePlayer = (teamIndex, playerIndex) => {
    const newTeams = [...teams];
    newTeams[teamIndex].players.splice(playerIndex, 1);
    setTeams(newTeams);
  };

  const addQuestion = () => {
    setQuestions([...questions, { text: '', answers: [{ text: '', responses: 0, score: 0 }] }]);
  };

  const updateQuestion = (qIndex, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].text = value;
    setQuestions(newQuestions);
  };

  const addAnswer = (qIndex) => {
    const newQuestions = [...questions];
    if (newQuestions[qIndex].answers.length < 10) {
      newQuestions[qIndex].answers.push({ text: '', responses: 0, score: 0 });
      setQuestions(newQuestions);
    }
  };

  const updateAnswer = (qIndex, aIndex, field, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers[aIndex][field] = field === 'text' ? value : parseInt(value) || 0;
    setQuestions(newQuestions);
  };

  const removeAnswer = (qIndex, aIndex) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].answers.splice(aIndex, 1);
    setQuestions(newQuestions);
  };

  const removeQuestion = (qIndex) => {
    const newQuestions = [...questions];
    newQuestions.splice(qIndex, 1);
    setQuestions(newQuestions);
  };

  const startGame = () => {
    window.open('#/rubric', '_blank');
    navigate('/game');
  };

  const getThemeClasses = () => {
    const themes = {
      'default': {
        teamInactive: 'border-transparent bg-blue-900/95 shadow-md',
        teamActive: 'border-yellow-400 bg-blue-800 scale-105 shadow-lg',
        questionHeader: 'bg-blue-900',
        answerFront: 'from-blue-600 to-blue-800 border-blue-400',
        answerFrontCircle: 'bg-blue-900 border-blue-300',
        answerBack: 'from-yellow-500 to-yellow-600 text-blue-900 border-yellow-300',
        answerBackDivider: 'border-blue-900'
      },
      'modern-dark': {
        teamInactive: 'border-transparent bg-gray-800/95 shadow-md',
        teamActive: 'border-cyan-400 bg-gray-700 scale-105 shadow-lg',
        questionHeader: 'bg-gray-800 border border-gray-600',
        answerFront: 'from-gray-700 to-gray-900 border-gray-500',
        answerFrontCircle: 'bg-gray-800 border-gray-400',
        answerBack: 'from-cyan-500 to-cyan-600 text-gray-900 border-cyan-300',
        answerBackDivider: 'border-gray-900'
      },
      'classic-gold': {
        teamInactive: 'border-transparent bg-amber-900/95 shadow-md',
        teamActive: 'border-yellow-200 bg-amber-800 scale-105 shadow-lg',
        questionHeader: 'bg-amber-900 border border-amber-700',
        answerFront: 'from-amber-700 to-amber-800 border-amber-500',
        answerFrontCircle: 'bg-amber-900 border-amber-400',
        answerBack: 'from-yellow-400 to-yellow-500 text-amber-950 border-yellow-200',
        answerBackDivider: 'border-amber-950'
      },
      'emerald': {
        teamInactive: 'border-transparent bg-emerald-900/95 shadow-md',
        teamActive: 'border-green-300 bg-emerald-800 scale-105 shadow-lg',
        questionHeader: 'bg-emerald-900 border border-emerald-700',
        answerFront: 'from-emerald-700 to-emerald-800 border-emerald-500',
        answerFrontCircle: 'bg-emerald-900 border-emerald-400',
        answerBack: 'from-green-400 to-green-500 text-emerald-950 border-green-200',
        answerBackDivider: 'border-emerald-950'
      },
      'royal-purple': {
        teamInactive: 'border-transparent bg-purple-900/95 shadow-md',
        teamActive: 'border-fuchsia-300 bg-purple-800 scale-105 shadow-lg',
        questionHeader: 'bg-purple-900 border border-purple-700',
        answerFront: 'from-purple-700 to-purple-800 border-purple-500',
        answerFrontCircle: 'bg-purple-900 border-purple-400',
        answerBack: 'from-fuchsia-400 to-fuchsia-500 text-purple-950 border-fuchsia-200',
        answerBackDivider: 'border-purple-950'
      },
      'sunset-orange': {
        teamInactive: 'border-transparent bg-orange-900/95 shadow-md',
        teamActive: 'border-amber-300 bg-orange-800 scale-105 shadow-lg',
        questionHeader: 'bg-orange-900 border border-orange-700',
        answerFront: 'from-orange-700 to-orange-800 border-orange-500',
        answerFrontCircle: 'bg-orange-900 border-orange-400',
        answerBack: 'from-amber-400 to-amber-500 text-orange-950 border-amber-200',
        answerBackDivider: 'border-orange-950'
      },
      'midnight-rose': {
        teamInactive: 'border-transparent bg-rose-900/95 shadow-md',
        teamActive: 'border-pink-300 bg-rose-800 scale-105 shadow-lg',
        questionHeader: 'bg-rose-900 border border-rose-700',
        answerFront: 'from-rose-700 to-rose-800 border-rose-500',
        answerFrontCircle: 'bg-rose-900 border-rose-400',
        answerBack: 'from-pink-400 to-pink-500 text-rose-950 border-pink-200',
        answerBackDivider: 'border-rose-950'
      },
      'ocean-breeze': {
        teamInactive: 'border-transparent bg-teal-900/95 shadow-md',
        teamActive: 'border-cyan-300 bg-teal-800 scale-105 shadow-lg',
        questionHeader: 'bg-teal-900 border border-teal-700',
        answerFront: 'from-teal-700 to-teal-800 border-teal-500',
        answerFrontCircle: 'bg-teal-900 border-teal-400',
        answerBack: 'from-cyan-400 to-cyan-500 text-teal-950 border-cyan-200',
        answerBackDivider: 'border-teal-950'
      },
      'slate-grey': {
        teamInactive: 'border-transparent bg-slate-800/95 shadow-md',
        teamActive: 'border-slate-300 bg-slate-700 scale-105 shadow-lg',
        questionHeader: 'bg-slate-800 border border-slate-600',
        answerFront: 'from-slate-600 to-slate-700 border-slate-500',
        answerFrontCircle: 'bg-slate-800 border-slate-400',
        answerBack: 'from-slate-300 to-slate-400 text-slate-900 border-slate-200',
        answerBackDivider: 'border-slate-900'
      },
      'cyberpunk': {
        teamInactive: 'border-transparent bg-indigo-900/95 shadow-md',
        teamActive: 'border-cyan-400 bg-indigo-800 scale-105 shadow-lg',
        questionHeader: 'bg-indigo-950 border border-pink-500 shadow-[0_0_10px_rgba(236,72,153,0.5)]',
        answerFront: 'from-indigo-800 to-purple-900 border-pink-500/50',
        answerFrontCircle: 'bg-indigo-950 border-pink-400 text-pink-400',
        answerBack: 'from-pink-500 to-fuchsia-600 text-indigo-950 border-cyan-300',
        answerBackDivider: 'border-indigo-950'
      }
    };
    return themes[theme] || themes['default'];
  };

  const themeClasses = getThemeClasses();

  return (
    <div className="container p-4 mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div className="flex-1"></div>
        <div className="flex space-x-2">
          <label className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center transition-colors cursor-pointer">
            <Upload size={18} className="mr-2" /> Import
            <input type="file" className="hidden" accept=".json" onChange={importGame} />
          </label>
          <button
            onClick={exportGame}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded flex items-center transition-colors"
          >
            <Download size={18} className="mr-2" /> Export
          </button>
          <button
            onClick={() => setIsResetModalOpen(true)}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded flex items-center transition-colors"
          >
            <Trash2 size={18} className="mr-2" /> Reset All
          </button>
        </div>
      </div>

      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl p-8 mb-8 text-white relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-3xl font-black mb-4 flex items-center">
            Welcome to Popular Vote Boards!
          </h2>
          <p className="text-lg text-blue-100 mb-6 max-w-6xl">
            Create, customize, and host your own survey-based party game. Perfect for classrooms, game nights, and team-building events.
            Set up your teams, add your questions and survey responses, and project the interactive game board for everyone to play!
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <div className="bg-blue-500 w-10 h-10 rounded-full flex items-center justify-center mb-3 font-bold text-xl shadow-inner">1</div>
              <h3 className="font-bold text-xl mb-2">Set Up Teams</h3>
              <p className="text-blue-100 text-sm">Add player names and custom avatars. Choose from built-in themes to match your event's vibe.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <div className="bg-purple-500 w-10 h-10 rounded-full flex items-center justify-center mb-3 font-bold text-xl shadow-inner">2</div>
              <h3 className="font-bold text-xl mb-2">Add Questions</h3>
              <p className="text-purple-100 text-sm">Enter survey questions and answers with their respective point values based on popular vote.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <div className="bg-pink-500 w-10 h-10 rounded-full flex items-center justify-center mb-3 font-bold text-xl shadow-inner">3</div>
              <h3 className="font-bold text-xl mb-2">Host & Play</h3>
              <p className="text-pink-100 text-sm">Launch the interactive game board in a new window. Use the control panel to reveal answers and award points!</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <div className="bg-amber-500 w-10 h-10 rounded-full flex items-center justify-center mb-3 font-bold text-xl shadow-inner">4</div>
              <h3 className="font-bold text-xl mb-2">Export for Later <span className="text-xs font-normal opacity-80">(Optional)</span></h3>
              <p className="text-amber-100 text-sm">Set up your game in advance and export it to a file so you can import and play it later.</p>
            </div>
          </div>
        </div>
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-64 h-64 bg-blue-400 opacity-20 rounded-full blur-2xl"></div>
      </div>

      <ConfirmationModal
        isOpen={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        onConfirm={handleReset}
        title="Reset All Forms?"
        message="Are you sure you want to reset all forms? This will clear all teams and questions. This action cannot be undone."
      />

      <div className="grid grid-cols-1 gap-8 mb-8 md:grid-cols-2">
        <div className="p-8 border rounded-2xl shadow-xl bg-white/80 backdrop-blur-lg border-white/40 md:col-span-2">
          <h2 className="flex items-center mb-6 text-2xl font-bold border-b pb-2"><Clock className="mr-2 text-blue-600" /> Game Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="flex flex-col space-y-2 text-left">
              <label className="font-bold text-gray-700">Timer Duration (seconds)</label>
              <div className="relative">
                <input
                  type="number"
                  className="w-full p-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-mono text-lg"
                  value={timerDuration}
                  onChange={(e) => setTimerDuration(parseInt(e.target.value) || 0)}
                  min="0"
                />
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              </div>
              <p className="text-sm text-gray-500">The countdown timer shown on the game board.</p>
            </div>
            <div className="flex flex-col space-y-2 text-left">
              <label className="font-bold text-gray-700">Background Image URL</label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full p-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                  placeholder="https://example.com/image.jpg"
                  value={backgroundImageUrl}
                  onChange={(e) => setBackgroundImageUrl(e.target.value)}
                />
                <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              </div>
              <p className="text-sm text-gray-500">The background image for the game board.</p>
            </div>
            <div className="flex flex-col space-y-2 text-left">
              <label className="font-bold text-gray-700">Game Theme</label>
              <div className="relative">
                <select
                  className="w-full p-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm appearance-none bg-white"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                >
                  <option value="default">Default Blue</option>
                  <option value="modern-dark">Modern Dark</option>
                  <option value="classic-gold">Classic Gold</option>
                  <option value="emerald">Emerald Forest</option>
                  <option value="royal-purple">Royal Purple</option>
                  <option value="sunset-orange">Sunset Orange</option>
                  <option value="midnight-rose">Midnight Rose</option>
                  <option value="ocean-breeze">Ocean Breeze</option>
                  <option value="slate-grey">Slate Grey</option>
                  <option value="cyberpunk">Cyberpunk</option>
                </select>
                <Plus className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 rotate-45" size={20} />
              </div>
              <p className="text-sm text-gray-500">Choose a color theme for the game board.</p>
            </div>
          </div>
        </div>
        {teams.map((team, tIdx) => (
          <div key={tIdx} className={`p-6 border rounded-2xl shadow-xl transition-all bg-white/80 backdrop-blur-lg border-white/40`}>
            <input
              className="w-full mb-4 text-2xl font-semibold border-b focus:outline-none bg-transparent"
              value={team.name}
              onChange={(e) => {
                const newTeams = [...teams];
                newTeams[tIdx].name = e.target.value;
                setTeams(newTeams);
              }}
            />
            <div className="space-y-3">
              {team.players.map((player, pIdx) => (
                <div key={pIdx} className="flex items-center space-x-2 bg-white/10 p-2 rounded">
                  {player.image && <img src={player.image} alt="player" className="w-10 h-10 rounded-full object-cover border border-white/20" />}
                  <div className="flex-1 flex flex-col space-y-1">
                    <input
                      placeholder="Player Name"
                      className="w-full p-2 border rounded bg-white text-gray-900"
                      value={player.name}
                      onChange={(e) => updatePlayer(tIdx, pIdx, 'name', e.target.value)}
                    />
                    <input
                      placeholder="Image URL"
                      className="w-full p-1 text-sm border rounded italic bg-white text-gray-900"
                      value={player.image || ''}
                      onChange={(e) => updatePlayer(tIdx, pIdx, 'image', e.target.value)}
                    />
                  </div>
                  <button onClick={() => removePlayer(tIdx, pIdx)} className="text-red-400 hover:text-red-300 transition-colors"><Trash2 size={20} /></button>
                </div>
              ))}
              <button onClick={() => addPlayer(tIdx)} className="flex items-center font-bold hover:underline"><Plus size={16} className="mr-1" /> Add Player</button>
            </div>
          </div>
        ))}
      </div>

      <div className="p-8 mb-8 border rounded-2xl shadow-xl bg-white/80 backdrop-blur-lg border-white/40">
        <h2 className="flex items-center mb-6 text-2xl font-bold"><HelpCircle className="mr-2" /> Questions</h2>
        <div className="space-y-6">
          {questions.map((q, qIdx) => (
            <div key={qIdx} className="p-4 border rounded bg-gray-50">
              <div className="flex items-center mb-3 space-x-2">
                <input
                  className="flex-1 p-2 text-lg font-medium border rounded"
                  placeholder="Question text"
                  value={q.text}
                  onChange={(e) => updateQuestion(qIdx, e.target.value)}
                />
                <button onClick={() => removeQuestion(qIdx)} className="text-red-500"><Trash2 size={24} /></button>
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {reorderAnswers(q.answers).map((a) => (
                  <div key={a.originalIdx} className="flex items-center p-2 space-x-1 border rounded bg-white">
                    <span className="w-6 text-gray-400">{a.originalIdx + 1}.</span>
                    <input
                      placeholder="Answer"
                      className="flex-1 p-1 border-b"
                      value={a.text}
                      onChange={(e) => updateAnswer(qIdx, a.originalIdx, 'text', e.target.value)}
                    />
                    <input
                      type="number"
                      placeholder="Resp"
                      className="w-14 p-1 border-b"
                      value={a.responses}
                      onChange={(e) => updateAnswer(qIdx, a.originalIdx, 'responses', e.target.value)}
                    />
                    <button onClick={() => removeAnswer(qIdx, a.originalIdx)} className="text-red-400"><Trash2 size={16} /></button>
                  </div>
                ))}
                {q.answers.length < 10 && (
                  <button onClick={() => addAnswer(qIdx)} className="flex items-center justify-center p-2 border border-dashed rounded text-blue-500 hover:bg-blue-50">
                    <Plus size={16} /> Add Answer
                  </button>
                )}
              </div>
            </div>
          ))}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={addQuestion}
            className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-md">
            <Plus className="mr-2" /> Add Question
          </motion.button>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="mb-4 text-2xl font-bold">Board Previews</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {questions.map((q, qIdx) => (
            <div key={qIdx} className={`p-4 border rounded shadow text-white bg-black/30 backdrop-blur-md border-gray-700`}>
              <h3 className={`mb-4 text-center border-b pb-2 font-bold uppercase tracking-wider p-2 rounded ${themeClasses.questionHeader}`}>{q.text || "Untitled Question"}</h3>
              <div className="grid grid-cols-2 gap-2">
                {reorderAnswers(q.answers).map((a) => (
                  <div key={a.originalIdx} className={`h-10 flex items-center justify-center border rounded font-bold bg-gradient-to-br ${themeClasses.answerFront}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs mr-2 border ${themeClasses.answerFrontCircle}`}>
                      {a.originalIdx + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {questions.length > 0 && (
        <div className="flex justify-center mb-10">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={startGame}
            className="flex items-center px-12 py-5 text-2xl font-black text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-[0_10px_20px_rgba(16,185,129,0.3)] hover:shadow-[0_15px_30px_rgba(16,185,129,0.4)] transition-shadow"
          >
            <Play className="mr-2" fill="white" size={32} /> START GAME
          </motion.button>
        </div>
      )}

      <div className="mt-12 mb-8 text-center text-gray-500 border-t pt-8">
        <p className="flex items-center justify-center font-medium">
          Created by
          <a href="https://plenumonary.com" className="text-blue-500 hover:underline ml-1" target="_blank" rel="noreferrer">
            Plenumonary Studios
          </a>
        </p>
        <p>
          <a href="https://iconscout.com/icons/bar-graph" className="text-underline font-size-sm" target="_blank" rel="noreferrer">
            Bar graph icon
          </a> by <a href="https://iconscout.com/contributors/graphic-room" className="text-underline font-size-sm" target="_blank" rel="noreferrer">
            Venus.
          </a> Wrong answer sound effect by <a href="https://pixabay.com/sound-effects/film-special-effects-wrong-answer-21-199825/" className="text-underline font-size-sm" target="_blank" rel="noreferrer">
            Tuomas Data.
          </a> Right answer sound effect by <a href="https://pixabay.com/sound-effects/film-special-effects-ding-101492/" className="text-underline font-size-sm" target="_blank" rel="noreferrer">
            Daronoxus.
          </a>
        </p>
      </div>
    </div>
  );
};

export default LandingPage;
